import { Fragment, useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const isItemActive = (item = {}) => {
  if (item.active) return true;

  if (item.url) {
    return false;
  }

  if (Array.isArray(item.children) && item.children.length) {
    return item.children.some((child) => isItemActive(child));
  }

  return false;
};

const mapLinkAttributes = (item = {}) => {
  const { url, newWindow } = item;

  if (!url) return {};

  const attrs = { href: url };

  if (newWindow) {
    attrs.target = '_blank';
    attrs.rel = 'noopener noreferrer';
  }

  return attrs;
};

const PRIMARY_TRANSITION = {
  enter: 'transition ease-smooth duration-500',
  enterFrom: 'opacity-0 translate-y-2',
  enterTo: 'opacity-100 translate-y-0',
  leave: 'transition ease-smooth duration-500',
  leaveFrom: 'opacity-100 translate-y-0',
  leaveTo: 'opacity-0 translate-y-2',
};

const SECONDARY_TRANSITION = {
  enter: 'transition ease-smooth duration-500',
  enterFrom: 'opacity-0 translate-y-1',
  enterTo: 'opacity-100 translate-y-0',
  leave: 'transition ease-smooth duration-500',
  leaveFrom: 'opacity-100 translate-y-0',
  leaveTo: 'opacity-0 translate-y-1',
};

function useDropdownController() {
  const [openIndex, setOpenIndex] = useState(null);
  const [readyIndex, setReadyIndex] = useState(null);
  const lastTapRef = useRef({ index: null, time: 0 });
  const isTouchLikeRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasHover =
      window.matchMedia &&
      window.matchMedia('(hover: hover)').matches &&
      window.matchMedia('(pointer: fine)').matches;

    isTouchLikeRef.current = !hasHover;
  }, []);

  const bindHandlers = (index, hasChildren, itemUrl) => {
    const handleMouseEnter = () => {
      setOpenIndex(index);
      setReadyIndex(null);
    };

    const handleMouseLeave = () => {
      setOpenIndex(null);
      setReadyIndex(null);
    };


    const handleClick = (event) => {
      if (!hasChildren) return;

      if (!isTouchLikeRef.current) {
        // Desktop/pointer: let the link behave normally.
        return;
      }

      event.preventDefault();

      if (openIndex === index) {
        setOpenIndex(null);
        setReadyIndex(null);
        lastTapRef.current = { index: null, time: 0 };
        return;
      }

      setOpenIndex(index);
      setReadyIndex(index);
      lastTapRef.current = { index, time: Date.now() };
    };

    const handleFocus = () => {
      if (hasChildren) {
        setOpenIndex(index);
        setReadyIndex(index);
      }
    };

    const handleBlur = (event) => {
      if (!hasChildren) return;
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setOpenIndex(null);
        setReadyIndex(null);
        lastTapRef.current = { index: null, time: 0 };
      }
    };

    return {
      isOpen: openIndex === index,
      handleMouseEnter,
      handleMouseLeave,
      handleClick,
      handleFocus,
      handleBlur,
    };
  };

  return { bindHandlers, isTouchLikeRef, openIndex };
}

function PrimaryNavigation({ navigationId, ariaLabel, items, megaMenuCta = null }) {
  const { bindHandlers, isTouchLikeRef, openIndex } = useDropdownController();
  const triggerRefs = useRef([]);
  const dropdownRefs = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      if (openIndex === null) return;

      const dropdown = dropdownRefs.current[openIndex];
      const trigger = triggerRefs.current[openIndex];

      if (!dropdown || !trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const header = dropdown.closest('.g-header');
      const headerRect = header ? header.getBoundingClientRect() : null;
      const top = headerRect
        ? triggerRect.bottom - headerRect.top
        : triggerRect.bottom + window.scrollY;
      dropdown.style.top = `${top}px`;

      // Keep CSS centering intact; just update arrow offset
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const viewportCenter = window.innerWidth / 2;
      const offset = triggerCenter - viewportCenter;
      dropdown.style.setProperty('--arrow-offset', `${offset}px`);
    };

    if (openIndex !== null) {
      rafRef.current = requestAnimationFrame(measure);
      window.addEventListener('resize', measure);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      window.removeEventListener('resize', measure);
    };
  }, [openIndex]);

  const hasMegaMenuCta = Boolean(megaMenuCta?.url);
  const megaMenuCtaHeading = megaMenuCta?.heading || megaMenuCta?.title || null;

  return (
    <nav id={navigationId} aria-label={ariaLabel} className="g-header__primary-navigation">
      <ul className="g-header__primary-list">
        {items.map((item, index) => {
          const key = `${item.title || 'item'}-${index}`;
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const isActive = isItemActive(item);
          const itemClassName = classNames(
            'g-header__primary-item',
            hasChildren && 'has-children',
          );

          if (!hasChildren) {
            return (
              <li key={key} className={itemClassName}>
                <a
                  {...mapLinkAttributes(item)}
                  className={classNames(
                    'g-header__primary-link',
                    isActive && 'is-active',
                    item.classes,
                  )}
                  title={item.title || undefined}
                >
                  {item.title}
                </a>
              </li>
            );
          }

          const { isOpen, handleMouseEnter, handleMouseLeave, handleClick, handleFocus, handleBlur } =
            bindHandlers(index, hasChildren, item.url);

          return (
            <li
              key={key}
              className={itemClassName}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onBlur={handleBlur}
            >
              <a
                {...mapLinkAttributes(item)}
                className={classNames(
                  'g-header__primary-link',
                  isActive && 'is-active',
                  isTouchLikeRef.current && isOpen && 'is-active',
                  item.classes,
                )}
                title={item.title || undefined}
                aria-expanded={isOpen}
                onClick={handleClick}
                onFocus={handleFocus}
                ref={(el) => {
                  triggerRefs.current[index] = el;
                }}
              >
                {item.title}
              </a>
              <Transition
                as={Fragment}
                show={isOpen}
                enter={PRIMARY_TRANSITION.enter}
                enterFrom={PRIMARY_TRANSITION.enterFrom}
                enterTo={PRIMARY_TRANSITION.enterTo}
                leave={PRIMARY_TRANSITION.leave}
                leaveFrom={PRIMARY_TRANSITION.leaveFrom}
                leaveTo={PRIMARY_TRANSITION.leaveTo}
              >
                <div
                  className="g-header__primary-dropdown"
                  ref={(el) => {
                    dropdownRefs.current[index] = el;
                  }}
                >
                  <div className="g-header__primary-dropdown-panel">
                    <div className="g-header__primary-dropdown-header">
                      <div className="g-header__primary-dropdown-inner">
                        <strong>{item.title}</strong>
                        <a {...mapLinkAttributes(item)}>{item.title} Overview</a>
                      </div>
                    </div>
                    <div className="g-header__primary-dropdown-content">
                      <div className="g-header__primary-dropdown-inner">
                        <ul className="g-header__primary-dropdown-list">
                          {item.children.map((child, childIndex) => {
                            const childKey = `${key}-child-${childIndex}`;
                            const hasGrandchildren = Array.isArray(child.children) && child.children.length > 0;

                            return (
                              <li key={childKey} className="g-header__primary-dropdown-item">
                                <a
                                  {...mapLinkAttributes(child)}
                                  className={classNames(
                                    'g-header__primary-dropdown-link',
                                    child.classes,
                                  )}
                                  title={child.title || undefined}
                                >
                                  {child.title}
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" fill="none" viewBox="0 0 12 13"><path stroke="#001e62" strokeWidth="1.5" d="M0 6.541h10.096M5.288.506l5.288 5.798-5.288 5.798" /></svg>
                                </a>

                                {hasGrandchildren && (
                                  <ul className="g-header__primary-dropdown-sublist">
                                    {child.children.map((grandchild, grandchildIndex) => (
                                      <li
                                        key={`${childKey}-grandchild-${grandchildIndex}`}
                                        className="g-header__primary-dropdown-subitem"
                                      >
                                        <a
                                          {...mapLinkAttributes(grandchild)}
                                          className={classNames(
                                            'g-header__primary-dropdown-sublink',
                                            grandchild.classes,
                                          )}
                                          title={grandchild.title || undefined}
                                        >
                                          {grandchild.title}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      {hasMegaMenuCta && (
                        <div className="g-header__primary-dropdown-inner">
                          <a
                            {...mapLinkAttributes(megaMenuCta)}
                            className="g-header__primary-dropdown-cta"
                            title={megaMenuCta.title || megaMenuCtaHeading || undefined}
                          >
                            <div className="g-header__primary-dropdown-cta-text">
                              {megaMenuCtaHeading && (
                                <span className="g-header__primary-dropdown-cta-heading">{megaMenuCtaHeading}</span>
                              )}
                              {megaMenuCta?.message && (
                                <span className="g-header__primary-dropdown-cta-body">{megaMenuCta.message}</span>
                              )}
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" viewBox="0 0 13 13"><path stroke="#e14a9b" strokeWidth="1.333" d="M0 6.165h12M6.353.457l5.647 6-5.647 6" /></svg>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Transition>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function SecondaryNavigation({ navigationId, ariaLabel, items }) {
  const { bindHandlers, isTouchLikeRef } = useDropdownController();

  return (
    <nav id={navigationId} aria-label={ariaLabel} className="g-header__secondary-navigation">
      <ul className="g-header__secondary-list">
        {items.map((item, index) => {
          const key = `${item.title || 'item'}-${index}`;
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const isActive = isItemActive(item);
          const itemClassName = classNames(
            'g-header__secondary-item',
            hasChildren && 'has-children',
          );

          if (!hasChildren) {
            return (
              <li key={key} className={itemClassName}>
                <a
                  {...mapLinkAttributes(item)}
                  className={classNames(
                    'g-header__secondary-link',
                    isActive && 'is-active',
                    item.classes,
                  )}
                  title={item.title || undefined}
                >
                  {item.title}
                </a>
              </li>
            );
          }

          const { isOpen, handleMouseEnter, handleMouseLeave, handleClick, handleFocus, handleBlur } =
            bindHandlers(index, hasChildren, item.url);

          return (
            <li
              key={key}
              className={itemClassName}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onBlur={handleBlur}
            >
              <a
                {...mapLinkAttributes(item)}
                className={classNames(
                  'g-header__secondary-link',
                  'g-header__secondary-trigger',
                  isActive && 'is-active',
                  isTouchLikeRef.current && isOpen && 'is-active',
                  item.classes,
                )}
                title={item.title || undefined}
                aria-expanded={isOpen}
                onClick={handleClick}
                onFocus={handleFocus}
              >
                {item.title}
              </a>
              <Transition
                as={Fragment}
                show={isOpen}
                enter={SECONDARY_TRANSITION.enter}
                enterFrom={SECONDARY_TRANSITION.enterFrom}
                enterTo={SECONDARY_TRANSITION.enterTo}
                leave={SECONDARY_TRANSITION.leave}
                leaveFrom={SECONDARY_TRANSITION.leaveFrom}
                leaveTo={SECONDARY_TRANSITION.leaveTo}
              >
                <div className="g-header__secondary-dropdown">
                  <div className="g-header__secondary-panel">
                    <a href={item.url} title={`${item.title} Overview`} className="g-header__secondary-dropdown-heading">{item.title} Overview</a>
                    <ul className="g-header__secondary-dropdown-list">
                      {item.children.map((child, childIndex) => (
                        <li className="g-header__secondary-dropdown-item">
                          <a
                            key={`${key}-child-${childIndex}`}
                            {...mapLinkAttributes(child)}
                            className={classNames(
                              'g-header__secondary-dropdown-link',
                              child.classes,
                            )}
                            title={child.title || undefined}
                          >
                            {child.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Transition>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default function Navigation({
  navigationId = 'header-navigation',
  ariaLabel = 'Primary navigation',
  items = [],
  variant = 'primary',
  megaMenuCta = null,
}) {
  if (variant === 'secondary') {
    return (
      <SecondaryNavigation
        navigationId={navigationId}
        ariaLabel={ariaLabel}
        items={items}
      />
    );
  }

  return (
    <PrimaryNavigation
      navigationId={navigationId}
      ariaLabel={ariaLabel}
      items={items}
      megaMenuCta={megaMenuCta}
    />
  );
}
